<template>
    <q-dialog v-model="show" @hide="$emit('close')">
        <q-card class="device-details">
            <q-bar
                class="bg-primary text-white"
                color="white"
                :class="{ 'q-py-lg': $q.screen.lt.md }"
            >
                <div>{{ $t('Device Details') }}</div>
                <q-space />
                <q-btn
                    @click="$emit('close')"
                    dense
                    :class="{ 'q-pa-sm': $q.screen.lt.md }"
                    :size="$q.screen.lt.md ? 'md' : 'sm'"
                    flat
                    icon="close"
                >
                    <q-tooltip>{{ $t('Close') }}</q-tooltip>
                </q-btn>
            </q-bar>
            <q-card-section class="main-card-section">
                <div class="row q-col-gutter-md">
                    <div class="col-md-3 col-xs-12">
                        <div class="row items-center q-mb-lg">
                            <div class="col-12">
                                <span class="text-h5 text-weight-medium">
                                    {{ device.serialNumber }}
                                </span>
                                <span>
                                    <q-icon
                                        v-if="device.notifications.isNotified"
                                        name="fa-light fa-bell-on"
                                        color="secondary"
                                        size="xs"
                                        class="q-pl-sm"
                                    >
                                        <q-tooltip>
                                            {{
                                                $t(
                                                    'You will be notified if the gateway disconnects.'
                                                )
                                            }}
                                        </q-tooltip>
                                    </q-icon>
                                </span>
                            </div>

                            <div class="col-12 text-weight-medium text-grey-8">
                                {{ facilityName }}
                            </div>
                        </div>
                        <div>
                            <div
                                class="text-body1 text-weight-medium q-mb-sm text-secondary"
                            >
                                {{ $t('Device Details') }}
                            </div>
                            <q-list bordered separator class="details-list">
                                <q-item>
                                    <q-item-section>
                                        <q-item-label caption>
                                            {{ $t('Type of Device') }}
                                        </q-item-label>
                                        <q-item-label>
                                            {{
                                                device.deviceType ||
                                                $t('Not provided')
                                            }}
                                        </q-item-label>
                                    </q-item-section>
                                </q-item>
                                <q-item>
                                    <q-item-section>
                                        <q-item-label caption>
                                            {{ $t('MAC Address') }}
                                        </q-item-label>
                                        <q-item-label>
                                            {{
                                                device.macAddress ||
                                                $t('Not provided')
                                            }}
                                        </q-item-label>
                                    </q-item-section>
                                </q-item>
                                <q-item>
                                    <q-item-section>
                                        <q-item-label caption>
                                            {{ $t('IP Address') }}
                                        </q-item-label>
                                        <q-item-label>
                                            {{
                                                device.ipAddress ||
                                                $t('Not provided')
                                            }}
                                        </q-item-label>
                                    </q-item-section>
                                </q-item>
                            </q-list>
                        </div>
                        <div>
                            <div
                                class="text-body1 text-weight-medium q-my-md text-secondary"
                            >
                                {{ $t('Linked Assets') }}
                            </div>
                            <device-linked-assets-table
                                style="height: 291px"
                                :assets="device.assets"
                                :facilityId="facilityId"
                            />
                        </div>
                    </div>
                    <div class="col-md-9 col-xs-12">
                        <q-separator
                            v-if="$q.screen.lt.md"
                            class="q-mb-md"
                            inset
                        />
                        <div class="row q-col-gutter-md justify-end">
                            <date-time-picker
                                v-model="dateTimePicker.start"
                                :dense="true"
                                :label="
                                    $t('Start') + ' (' + localTimezone + ')'
                                "
                                class="col-md-4 col-xs-12"
                                :disable="table.isLoading"
                            />
                            <date-time-picker
                                v-model="dateTimePicker.end"
                                :dense="true"
                                :label="$t('End') + ' (' + localTimezone + ')'"
                                class="col-md-4 col-xs-12"
                                :disable="table.isLoading"
                            />
                            <div class="col-md-2 col-xs-12">
                                <q-btn
                                    :label="$t('Generate')"
                                    :class="{
                                        'full-width q-mb-md': $q.screen.lt.md
                                    }"
                                    class="ellipsis"
                                    size="15px"
                                    type="submit"
                                    color="positive"
                                    :disable="table.isLoading"
                                    style="line-height: 0px; width: 100%"
                                    @click="
                                        onRequest({
                                            pagination: table.pagination
                                        })
                                    "
                                />
                            </div>
                        </div>

                        <q-table
                            :title="$t('Disconnections')"
                            @request="onRequest"
                            :rows="table.data"
                            :columns="getColumns"
                            v-model:pagination="table.pagination"
                            :rows-per-page-options="[
                                table.pagination.rowsPerPage
                            ]"
                            row-key="id"
                            :no-data-label="$t('No disconnections')"
                            class="main-table col-12"
                            binary-state-sort
                            :loading="table.isLoading"
                        />
                    </div>
                </div>
            </q-card-section>
        </q-card>
    </q-dialog>
</template>
<script>
import DateTimePicker from '@components/DateTimePicker.vue';
import DeviceLinkedAssetsTable from './DeviceLinkedAssetsTable.vue';
import DeviceManager from './deviceManager.js';
import UpdateURL from '@mixins/UpdateURL.vue';
import { mapGetters } from 'vuex';
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'DeviceDetails',
    mixins: [UpdateURL],
    components: { DateTimePicker, DeviceLinkedAssetsTable },
    emits: ['close'],
    props: {
        facilityId: {
            type: [Number, String],
            required: true
        },
        device: {
            type: Object,
            required: true,
            validator(propValue) {
                if (typeof propValue !== 'object' || propValue === null) {
                    return false;
                }

                // Check if prop has essential properties
                const requiredProps = [
                    'id',
                    'uuid',
                    'facility',
                    'serialNumber',
                    'assets'
                ];

                for (const prop of requiredProps) {
                    if (!Object.hasOwn(propValue, prop)) {
                        return false;
                    }
                }

                // Check essential property types
                if (
                    typeof propValue.id !== 'number' ||
                    typeof propValue.uuid !== 'string' ||
                    typeof propValue.facility !== 'number' ||
                    typeof propValue.serialNumber !== 'string' ||
                    !Array.isArray(propValue.assets)
                ) {
                    return false;
                }

                // Check assets array items
                for (const asset of propValue.assets) {
                    if (
                        !Object.hasOwn(asset, 'id') ||
                        !Object.hasOwn(asset, 'name') ||
                        !Object.hasOwn(asset, 'uuid')
                    ) {
                        console.warn(
                            'Device validation failed: asset missing required properties'
                        );
                        return false;
                    }

                    if (
                        typeof asset.id !== 'number' ||
                        typeof asset.name !== 'string' ||
                        typeof asset.uuid !== 'string'
                    ) {
                        return false;
                    }
                }

                return true;
            }
        },
        start: {
            type: String,
            required: true
        },
        end: {
            type: String,
            required: true
        }
    },
    created() {
        this.dateTimePicker.start = this.start;
        this.dateTimePicker.end = this.end;
    },
    mounted() {
        this.deviceManager = new DeviceManager(this.localTimezone);
        this.getDisconnections();
    },
    computed: {
        ...mapGetters({
            getFacility: 'facilities/getFacility'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Get facility name
         * @return {String}
         */
        facilityName() {
            if (this.getFacility(this.facilityId)) {
                return this.getFacility(this.facilityId).name;
            }
            return null;
        },
        /**
         * Get columns for the disconnections table
         * @return {Array}
         */
        getColumns() {
            return [
                {
                    name: 'start',
                    label:
                        this.$t('Start Date') + ' (' + this.localTimezone + ')',
                    field: 'start',
                    align: 'left',
                    sortable: true,
                    format: val => {
                        return this.getFormattedDate(val);
                    }
                },
                {
                    name: 'end',
                    label:
                        this.$t('End Date') + ' (' + this.localTimezone + ')',
                    field: 'end',
                    align: 'left',
                    sortable: true,
                    format: val => {
                        return this.getEndDate(val);
                    }
                },
                {
                    name: 'duration_s',
                    label: this.$t('Duration'),
                    field: 'duration_s',
                    align: 'left',
                    sortable: true,
                    format: val => {
                        return this.getFormattedDuration(val);
                    }
                },
                {
                    name: 'description',
                    label: this.$t('Description'),
                    field: 'description',
                    align: 'left',
                    sortable: true
                }
            ];
        }
    },
    data() {
        return {
            show: true,

            deviceManager: null,

            localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            dateTimePicker: {
                start: null,
                end: null
            },
            table: {
                data: [],
                pagination: {
                    sortBy: 'end',
                    descending: true,
                    page: 1,
                    rowsPerPage: 10,
                    rowsNumber: 0
                },
                isLoading: false
            }
        };
    },
    methods: {
        async getDisconnections() {
            const { sortBy, descending, page } = this.table.pagination;
            this.table.isLoading = true;
            const disconnectionsData =
                await this.deviceManager.getAllDisconnections({
                    device: this.device.id,
                    ordering: descending ? `-${sortBy}` : `${sortBy}`,
                    page: page,
                    start__lte: this.dateTimePicker.end,
                    end__gte: this.dateTimePicker.start
                });
            // update table data
            this.table.data = disconnectionsData.results;
            // update total items count
            this.table.pagination.rowsNumber = disconnectionsData.count;
            this.table.isLoading = false;
        },
        /**
         * Get the formatted date
         * @return {String}
         */
        getFormattedDate(date) {
            return new Date(date).toLocaleString(this.userLocale, {
                month: 'short',
                day: '2-digit',
                hour: 'numeric',
                minute: '2-digit',
                second: '2-digit',
                timezone: this.localTimezone
            });
        },
        /**
         * Get end date or unfinished for events with no end date
         * @return {String}
         */
        getEndDate(date) {
            if (date) {
                return this.getFormattedDate(date);
            }
            return '-';
        },
        /**
         * Returns total of disconnected time formatted.
         * @return {String} - 99h 59min / 59min 15sec / 15sec
         */
        getFormattedDuration(totalSeconds) {
            if (!totalSeconds) {
                return '-';
            }
            if (totalSeconds < 60) {
                return `${totalSeconds} seconds`;
            }
            const totalMinutes = Math.floor(totalSeconds / 60);
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            const seconds = totalSeconds % 60;

            if (hours > 0) {
                return this.$t('{hours}h {minutes}min', {
                    hours: hours,
                    minutes: minutes
                });
            } else {
                return this.$t('{minutes}min {seconds}sec', {
                    minutes: minutes,
                    seconds: seconds
                });
            }
        },
        /**
         * Refetch the results from the server.
         */
        async refreshList() {
            // set current date and time to "now"
            // fetch data from the server
            await this.onRequest({
                pagination: this.table.pagination,
                filter: ''
            });
        },
        async onRequest(props) {
            // check that the start date is before the end date
            if (this.dateTimePicker.start >= this.dateTimePicker.end) {
                this.$notifyError(
                    this.$t('Start date must come before the end date.')
                );
                return;
            }

            // update pagination object
            this.table.pagination = props.pagination;
            // clear table for new data
            this.table.data = [];
            await this.getDisconnections();
        }
    }
};
</script>
<style lang="scss" scoped>
.device-details {
    width: 100%;
    max-width: 1200px;
    overflow: hidden;
}

.details-list {
    border-radius: 4px;
}
.main-card-section {
    height: calc(100% - 48px);
    max-height: 720px;
    overflow: auto;
}
.main-table {
    height: 100%;
    max-height: 553px;
    margin-bottom: 16px;
}
</style>
